/**
 * User experience preferences — how a user wants the navigation arranged
 * within the space the admin policy and their permissions allow.
 *
 * These are HOW-I-WORK choices, not access. The resolver treats them as the
 * lowest-priority input: a hidden choice can be overridden by an admin lock,
 * and a pin can never reveal an item the user lacks permission for.
 *
 * Persistence mirrors the locale / display-size pattern: localStorage-only
 * today, behind a small read/write/coerce seam so a future server-side
 * `user_preferences` column can back it without touching consumers. The
 * `displaySize` comfort control keeps its own dedicated provider/storage
 * (`@/lib/display-size`); it is referenced from the same settings area but not
 * duplicated here. `sidebarMode` is reserved for a future compact rail and is
 * persisted but not yet applied.
 */
import { isNavItemId } from "./nav-catalog";
import { isWorkProfile, DEFAULT_WORK_PROFILE, type WorkProfile } from "./work-profiles";

export type UserExperiencePreferences = {
  workProfile: WorkProfile;
  /** Preferred landing nav-item id (validated against visibility at resolve). */
  startPage?: string;
  /** Nav-item ids the user explicitly pins visible (re-adds within their rights). */
  pinnedNavItems: string[];
  /** Nav-item ids the user explicitly hides. */
  hiddenNavItems: string[];
  /** Reserved — future compact sidebar. Persisted, not yet applied. */
  sidebarMode?: "expanded" | "compact";
};

export const DEFAULT_EXPERIENCE_PREFERENCES: UserExperiencePreferences = {
  workProfile: DEFAULT_WORK_PROFILE,
  startPage: undefined,
  pinnedNavItems: [],
  hiddenNavItems: [],
  sidebarMode: "expanded",
};

/** localStorage key — namespaced like `fixcontrol.locale` / `.displaySize`. */
export const EXPERIENCE_PREFS_STORAGE_KEY = "fixcontrol.navPrefs";

function sanitiseIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of value) {
    if (isNavItemId(v) && !seen.has(v)) {
      seen.add(v);
      out.push(v);
    }
  }
  return out;
}

/** Coerce any untrusted value into valid preferences; never throws. */
export function coerceExperiencePreferences(raw: unknown): UserExperiencePreferences {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_EXPERIENCE_PREFERENCES };
  const r = raw as Record<string, unknown>;
  const hidden = sanitiseIds(r.hiddenNavItems);
  const hiddenSet = new Set(hidden);
  // A pin and a hide of the same id is contradictory — the pin wins (it's the
  // more recent positive intent), so drop it from the hidden set's shadow.
  const pinned = sanitiseIds(r.pinnedNavItems).filter((id) => !hiddenSet.has(id));
  return {
    workProfile: isWorkProfile(r.workProfile) ? r.workProfile : DEFAULT_WORK_PROFILE,
    startPage: isNavItemId(r.startPage) ? r.startPage : undefined,
    pinnedNavItems: pinned,
    hiddenNavItems: hidden,
    sidebarMode: r.sidebarMode === "compact" ? "compact" : "expanded",
  };
}

export function readStoredExperiencePreferences(): UserExperiencePreferences {
  if (typeof window === "undefined") return { ...DEFAULT_EXPERIENCE_PREFERENCES };
  try {
    const raw = window.localStorage.getItem(EXPERIENCE_PREFS_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_EXPERIENCE_PREFERENCES };
    return coerceExperiencePreferences(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_EXPERIENCE_PREFERENCES };
  }
}

export function writeStoredExperiencePreferences(prefs: UserExperiencePreferences): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      EXPERIENCE_PREFS_STORAGE_KEY,
      JSON.stringify(coerceExperiencePreferences(prefs)),
    );
  } catch {
    /* ignore — private mode / quota */
  }
}
